import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  DeleteTicketSectionMutationInput, Mutation, Query, SaveTicketSectionMutationInput, TicketSectionAttributes,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as TicketSection from './TicketSection';
import { TICKET_SECTION_FIELDS } from './fragments';

// `event.id` lets the result normalize onto the cached `Event`, as `EVENT_PRESALES_QUERY` does.
export const EVENT_TICKET_SECTIONS_QUERY = gql`
  query EventTicketSections($eventId: Int!) {
    event(id: $eventId) {
      id
      sections {
        ...TicketSectionFields
      }
    }
  }
  ${TICKET_SECTION_FIELDS}
`;

export const SAVE_TICKET_SECTION_MUTATION = gql`
  mutation saveTicketSection($input: SaveTicketSectionMutationInput!) {
    saveTicketSection(input: $input) {
      ticketSection {
        ...TicketSectionFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TICKET_SECTION_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** An event's ticket sections in server order; the server refuses the read without `view_tickets?`. */
export const fetchTicketSections = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<VPResult<TicketSection.TicketSection[], VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: EVENT_TICKET_SECTIONS_QUERY,
    fetchPolicy: 'network-only',
    variables: { eventId },
  });

  if (!data?.event?.sections) {
    return { success: false, errors: [{ path: '', message: 'Failed to fetch the ticket sections.' }] };
  }

  return { success: true, data: data.event.sections.map(TicketSection.fromGql) };
};

/** Saves a section from attributes the caller has already run through `TicketSectionInput.toGqlInput`. */
export const saveTicketSection = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketSection: TicketSectionAttributes,
): Promise<VPResult<TicketSection.TicketSection, VPErrors.VPError[]>> => {
  const input: SaveTicketSectionMutationInput = { ticketSection };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_TICKET_SECTION_MUTATION,
    variables: { input },
  });

  const payload = data?.saveTicketSection;

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.ticketSection) {
    return { success: false, errors: [{ path: '', message: 'Failed to save the section.' }] };
  }

  return { success: true, data: TicketSection.fromGql(payload.ticketSection) };
};

export const DELETE_TICKET_SECTION_MUTATION = gql`
  mutation deleteTicketSection($input: DeleteTicketSectionMutationInput!) {
    deleteTicketSection(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/** Deletes a section; a refusal (tickets or holds on it, a seating-chart event) resolves with the server's own reason. */
export const deleteTicketSection = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const input: DeleteTicketSectionMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_TICKET_SECTION_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteTicketSection;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  return { success: false, errors: [{ path: '', message: 'Failed to delete the section.' }] };
};
